/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC } from 'react';
import {
  PageSection,
  Filepond,
  EntityTypes,
  SetProfilePicture,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { throwErrorWithId } from '@pypestream/utils';

import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { useProStudioViewerRole } from '../../hooks';
import { AssetCard } from '../../components';
import { uploadFile } from '../../utils';

import './files.scss';

type Asset = {
  id: string;
  url: string;
};

/**
 * The media grid, moved verbatim out of the old standalone Files page into a tab. The
 * page title and tooltip now live in `FilesLayout`, so only the body is here.
 *
 * NOTE: this still WRITES through Bot Manager — uploads to `/rest/bot-manager/.../assets`,
 * and deletes through `deleteBotAsset` (see delete-asset.ts). Per PSTUDIO-3336 Bot Manager
 * is retired and `assetsFiles` + `agent-manager-sync` / `deleteAgentManagerFile` are its
 * replacement, but migrating Media was explicitly out of scope for this change — it is a
 * follow-up, and a small one now that the plumbing exists. Only the READ side (the grid) is
 * migrated, so for a v2 solution both writes fail against Bot Manager.
 */
export const MediaTab: FC = () => {
  const [t] = useTranslation();
  const { assets, botId, projectId } = useProStudioCtxSelector((ctx) => {
    const ctxAssets: Asset[] = ((ctx.selectedBot?.assets || []) as Asset[])[0]
      ?.id
      ? ((ctx.selectedBot?.assets || []) as Asset[])
      : [];

    const { customerId, botName, project } = ctx;

    return {
      botId: customerId && botName ? `${customerId}.${botName}` : undefined,
      assets: ctxAssets,
      projectId: project?.id,
    };
  });

  const isViewer = useProStudioViewerRole();

  const onUpload: SetProfilePicture = ({
    entityId,
    file,
    load,
    error,
    progress,
    abort,
    onChange,
  }) => {
    if (!projectId || !entityId) {
      throwErrorWithId('Project ID and bot ID are required');
    }

    const uploadUrl = `${
      import.meta.env.FE_ADMIN_URL
    }/rest/bot-manager/${projectId}/${entityId}/assets`;

    return uploadFile({
      url: uploadUrl,
      file: file as File,
      type: 'assetData',
      load,
      progress,
      abort,
      onChange,
      error: (e) => {
        sendProStudioEvent({
          type: 'showError',
          error: e,
        });
        error?.(e);
      },
    });
  };

  return (
    <PageSection test-id="ps-files-page-section" size="small">
      <div className="c-files-page__list">
        {!isViewer && (
          <Filepond
            test-id="ps-files-page-filepond"
            vertical
            allowMultiple
            className="c-files-page__list-item"
            entityType={EntityTypes.ASSET}
            entityId={botId}
            previewLabel={
              t('pro-studio/files:filepond.previewLabel', {
                defaultValue: 'Drag a file here',
              }) || ''
            }
            onUpload={onUpload}
            onChange={(props) => {
              if (!props?.data) {
                return;
              }

              sendProStudioEvent({
                type: 'updateAssets',
                // @ts-ignore
                data: props.data,
              });
            }}
          />
        )}

        {assets.map(({ id, url }) => (
          <AssetCard
            testId={`ps-files-page-asset-card-${id}`}
            key={id}
            id={id}
            url={url}
            viewOnly={isViewer}
          />
        ))}
      </div>
    </PageSection>
  );
};
